import { useAuthStore } from '../store/authStore';
import supabase from "./supabase";
import { clearAllCaches } from '../serviceWorkerRegistration';
import { useAuthStore } from '../store/authStore';

// Account deletion function
export async function deleteUserAccount(confirmText: string): Promise<{ success: boolean; message: string }> {
  try {
    // Validate confirmation text
    if (!confirmText || confirmText.toLowerCase() !== 'delete my account') {
      throw new Error('Please type "delete my account" to confirm');
    }

    // Get current session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session) {
      throw new Error('You must be signed in to delete your account');
    }

    // Call the delete account edge function
    const { data, error } = await supabase.functions.invoke('delete-user-account', {
      body: { confirmText },
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (error) {
      console.error('Edge function error:', error);
      throw new Error(error.message || 'Failed to delete account');
    }

    if (!data?.success) {
      throw new Error(data?.error || 'Failed to delete account');
    }

    return {
      success: true,
      message: 'Your account has been successfully deleted'
    };

  } catch (error: any) {
    console.error('Account deletion error:', error);
    throw new Error(error.message || 'Failed to delete account. Please try again.');
  }
}

export async function signInWithEmail(email: string, password: string) {
  try {
    console.log('Attempting email sign in...');
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) throw error;

    if (!data.user) {
      throw new Error('No user data returned from sign in');
    }
    const { data: bannedUser } = await supabase
      .from("banned_users")
      .select("user_id")
      .maybeSingle();

    if (bannedUser) {
      await handleBannedUser(); // 👈 This handles sign out + redirect
      return;
    }

    // Clear caches to ensure fresh content
    clearAllCaches();

    // Wait briefly for the trigger to create/update the profile
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Get the user profile
    const { data: profile, error: profileError } = await supabase
  .rpc('get_current_user_profile');

    if (profileError || !profile) {
      throw new Error('Failed to retrieve user profile');
    }

    // Set onboarding step based on profile completeness
    let initialStep = null;
    if (!profile.onboarding_completed) {
      if (!profile.full_name || !profile.display_name) {
        initialStep = 'name';
      } else if (!profile.professions?.length) {
        initialStep = 'profession';
      } else if (!profile.experience_level) {
        initialStep = 'experience';
      } else if (!profile.location) {
        initialStep = 'location';
      } else if (!profile.goals) {
        initialStep = 'goals';
      } else if (!profile.terms_accepted) {
        initialStep = 'terms';
      }
    }

    useAuthStore.getState().setIsNewUser(false);
    useAuthStore.getState().setOnboardingStep(initialStep);
    useAuthStore.getState().setUser(profile);
    useAuthStore.getState().setIsOAuth(false);
    
    console.log('Sign in successful:', { data, profile, initialStep });
    return { data, profile };
  } catch (error: any) {
    console.error('Email auth error:', error);
    throw error;
  }
}

export async function signUpWithEmail(email: string, password: string, metadata?: Record<string, any>) {
  try {
    console.log('Attempting email sign up with metadata:', metadata);
    
    // First, check if user exists
    const { data: existingUser } = await supabase
      .from('user_profiles')
      .select('id')
      .eq('email', email)
      .maybeSingle();

    if (existingUser) {
      throw new Error('An account with this email already exists');
    }

    // Check password strength
    const passwordCheck = isPasswordSecure(password);
    if (!passwordCheck.isValid) {
      throw new Error(passwordCheck.message);
    }

    // Clear caches to ensure fresh content
    clearAllCaches();

    // Proceed with sign up
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.full_name || '',
          display_name: metadata?.display_name || '',
          avatar_url: metadata?.avatar_url || null,
          role: 'user',
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });

    if (error) {
      console.error('Signup error:', error);
      throw error;
    }

    if (!data.user) {
      throw new Error('No user data returned from signup');
    }

    // Wait briefly for the trigger to create the profile
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Update the user profile with the avatar URL
    if (metadata?.avatar_url) {
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({
          avatar_url: metadata.avatar_url,
          updated_at: new Date().toISOString()
        })

      if (updateError) {
        console.error('Failed to update avatar URL:', updateError);
      }
    }

    // Multiple attempts to get profile
    let profile = null;
    let attempts = 0;
    const maxAttempts = 5;

    while (!profile && attempts < maxAttempts) {
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .single();

      if (!profileError && profileData) {
        profile = profileData;
        break;
      }

      console.log(`Attempt ${attempts + 1} failed, retrying...`);
      await new Promise(resolve => setTimeout(resolve, 1000));
      attempts++;
    }

    if (!profile) {
      throw new Error('Failed to retrieve user profile after multiple attempts');
    }

    // Set initial onboarding step to 'name'
    useAuthStore.getState().setIsNewUser(true);
    useAuthStore.getState().setOnboardingStep('name');
    useAuthStore.getState().setUser(profile);
    useAuthStore.getState().setIsOAuth(false);

    console.log('Sign up successful:', { data, profile });
    return { data, profile };
  } catch (error: any) {
    console.error('Email auth error:', error);
    throw error;
  }
}

export async function signInWithGoogle() {
  try {
    // Clear caches to ensure fresh content
    clearAllCaches();
    
    // Store auth type in sessionStorage before redirect
    sessionStorage.setItem('authProvider', 'google');
    
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
          skip_confirmation: 'true'
        },
        redirectTo: `${window.location.origin}/auth/callback`
      }
    });

    if (error) throw error;
    return data;
  } catch (error: any) {
    console.error('Google auth error:', error);
    throw error;
  }
}

export async function signInWithApple() {
  try {
    // Clear caches to ensure fresh content
    clearAllCaches();
    
    // Store auth type in sessionStorage before redirect
    sessionStorage.setItem('authProvider', 'apple');
    
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: {
        queryParams: {
          response_mode: 'form_post',
          skip_confirmation: 'true'
        },
        redirectTo: `${window.location.origin}/auth/callback`
      }
    });

    if (error) throw error;
    return data;
  } catch (error: any) {
    console.error('Apple auth error:', error);
    throw error;
  }
}

// Function to update user email without requiring password
export async function updateUserEmail(newEmail: string) {
  try {
    const { data, error } = await supabase.auth.updateUser({
      email: newEmail
    });

    if (error) throw error;
    
    // Update the email in the user profile as well
    const { error: profileError } = await supabase
      .from('user_profiles')
      .update({ 
        email: newEmail,
        updated_at: new Date().toISOString() 
      })
      .eq('id', (await supabase.auth.getUser()).data.user?.id);
    
    if (profileError) {
      console.error('Error updating email in profile:', profileError);
    }
    
    return { 
      success: true, 
      message: 'Verification emails have been sent to your current and new email addresses. Please check both to complete the change.' 
    };
  } catch (error: any) {
    console.error('Email update error:', error);
    throw error;
  }
}

// Function to update user password with security checks
export async function updateUserPassword(currentPassword: string, newPassword: string) {
  try {
    // First verify current password
    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
      email: (await supabase.auth.getUser()).data.user?.email || '',
      password: currentPassword
    });

    if (authError) {
      throw new Error('Current password is incorrect');
    }

    // Check password strength
    const passwordCheck = isPasswordSecure(newPassword);
    if (!passwordCheck.isValid) {
      throw new Error(passwordCheck.message);
    }

    const { data, error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) throw error;
    
    return { 
      success: true, 
      message: 'Password updated successfully.' 
    };
  } catch (error: any) {
    console.error('Password update error:', error);
    throw error;
  }
}

// Function to check if a password is secure
export function isPasswordSecure(password: string): { isValid: boolean; message: string } {
  // Check minimum length
  if (password.length < 8) {
    return { isValid: false, message: 'Password must be at least 8 characters long' };
  }
  
  // Check for lowercase letters
  if (!/[a-z]/.test(password)) {
    return { isValid: false, message: 'Password must contain at least one lowercase letter' };
  }
  
  // Check for uppercase letters
  if (!/[A-Z]/.test(password)) {
    return { isValid: false, message: 'Password must contain at least one uppercase letter' };
  }
  
  // Check for digits
  if (!/\d/.test(password)) {
    return { isValid: false, message: 'Password must contain at least one digit' };
  }
  
  // Check for special characters
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    return { isValid: false, message: 'Password must contain at least one special character' };
  }
  
  // Check for common passwords (simplified version - in production, use a more comprehensive list)
  const commonPasswords = [
    'password', 'Password1', '12345678', 'qwerty123', 'admin123', 'welcome1',
    'letmein1', 'football', 'baseball', 'superman', 'trustno1', 'sunshine'
  ];
  
  if (commonPasswords.includes(password.toLowerCase())) {
    return { isValid: false, message: 'This password is too common and easily guessed' };
  }
  
  return { isValid: true, message: 'Password meets security requirements' };
}

export async function handleBannedUser() {
  await useAuthStore.getState().signOut();
  alert("🚫 Your account has been banned.");
  window.location.href = "/";
}